/**
 * The chips of a list page, as data: which groups a list has, what is chosen in each, and how the
 * URL spells it (#6712).
 *
 * A chip group holds the values of one field of the listed entity -- its stage, its category, the
 * reader's perspective on it. Chips in one group widen the list (OR): Drafts and Active together list
 * both. Separate groups narrow each other (AND): Active with Your move lists the active proposals
 * that wait on the reader. No chip in a group is that group's default, and a selection equal to the
 * default is spelled as no chip, so every list has one URL.
 *
 * The URL holds the selection as a repeated search parameter, `?stage=draft&stage=active`, read with
 * `getAll`; a link that names one value, as every link written before #6712 does, still parses.
 */

/** One chip: the value the URL spells, and the label the reader sees. */
export type ListChip<TValue extends string = string> = Readonly<{ value: TValue; label: string }>;

/** What is chosen in each group, by the group's search parameter. A group with nothing chosen is absent or empty. */
export type ListChipSelection = Readonly<Record<string, readonly string[]>>;

/** One group of chips: the values of one field. */
export type ListChipGroup<TValue extends string = string> = Readonly<{
  /** The search parameter the group's chips are written to, which also keys the group in a selection. */
  parameter: string;
  /** The field the chips are values of: the group's accessible name, and its label on a wide screen. */
  label: string;
  /** Every chip of the group, in the order they are drawn. */
  chips: readonly ListChip<TValue>[];
  /**
   * The values that list exactly what no chip lists, where no chip is a narrower list than every
   * value: on Proposals, Drafts and Active are the open list. Choosing all of them is no chip. Absent,
   * no chip lists every value, and no selection is the same as none.
   */
  sameAsNone?: readonly TValue[];
  /**
   * The chips that can match something given what the groups before this one have chosen, in the
   * order they are drawn: a draft waits on nobody else, so Their move has nothing to list there.
   * A chosen value that is not among them is dropped. Absent, every chip.
   */
  chipsGiven?: (selection: ListChipSelection) => readonly TValue[];
  /**
   * A field the list's API takes one value of. Pressing a chip replaces the one that is on rather than
   * joining it. Use it only until the API takes several: the standard is that chips in a group widen.
   */
  isSingle?: boolean;
  /**
   * What the group's menu says while the group lets everything through, or exactly `sameAsNone`:
   * "Open" for the Proposals stages. Absent, "All".
   */
  allLabel?: string;
}>;

/**
 * How a group is drawn (#6720). One chip is a switch, which is on or off. A group the API takes one
 * value of is a segmented control that starts with All. Any other group is a menu of checkboxes, so a
 * page with many values spends one control on them rather than a row of chips.
 */
export type ListChipControl = "switch" | "segmented" | "menu";

export function listChipControl(group: ListChipGroup): ListChipControl {
  if (group.chips.length === 1) return "switch";
  return group.isSingle ? "segmented" : "menu";
}

/** How many rows each chip matches, by group parameter and then by value; a count still on the wire is absent. */
export type ListChipCounts = Readonly<Record<string, Readonly<Partial<Record<string, number>>>>>;

/** The chips of `group` that can match something under `selection`. */
export function availableChips<TValue extends string>(
  group: ListChipGroup<TValue>,
  selection: ListChipSelection,
): readonly ListChip<TValue>[] {
  if (!group.chipsGiven) return group.chips;
  const available = group.chipsGiven(selection);
  return group.chips.filter((chip) => available.includes(chip.value));
}

/**
 * `selection` spelled the one way the URL spells it: each group in chip order, without a value the
 * group does not have or that cannot match given the groups before it, and empty where it lists
 * what no chip lists.
 */
export function normalizeListChips(groups: readonly ListChipGroup[], selection: ListChipSelection): ListChipSelection {
  const normalized: Record<string, readonly string[]> = {};
  for (const group of groups) {
    const chosen = new Set(selection[group.parameter] ?? []);
    const values = availableChips(group, normalized)
      .map((chip) => chip.value)
      .filter((value) => chosen.has(value));
    // A link that names every value a one-value group's default stands for names no choice, before
    // the group keeps only its first: `owner=mine&owner=others` is everybody's documents.
    normalized[group.parameter] = isSameAsNone(group, values) ? [] : values.slice(0, group.isSingle ? 1 : undefined);
  }
  return normalized;
}

function isSameAsNone(group: ListChipGroup, values: readonly string[]): boolean {
  const sameAsNone = group.sameAsNone;
  if (!sameAsNone) return false;
  return values.length === sameAsNone.length && sameAsNone.every((value) => values.includes(value));
}

/** The selection after the reader presses `value` in the group at `parameter`: on if it was off, off if it was on. */
export function toggleListChip(
  groups: readonly ListChipGroup[],
  selection: ListChipSelection,
  parameter: string,
  value: string,
): ListChipSelection {
  const chosen = selection[parameter] ?? [];
  const isSingle = groups.find((group) => group.parameter === parameter)?.isSingle === true;
  const next = chosen.includes(value)
    ? chosen.filter((chosenValue) => chosenValue !== value)
    : [...(isSingle ? [] : chosen), value];
  return normalizeListChips(groups, { ...selection, [parameter]: next });
}

/**
 * The selection after the reader ticks or unticks `value` in its group's menu. The menu ticks what the
 * group lets through, so with no chip on the Proposals stages show Drafts and Active ticked, and
 * unticking Drafts leaves Active.
 */
export function toggleListChipShown(
  groups: readonly ListChipGroup[],
  selection: ListChipSelection,
  parameter: string,
  value: string,
): ListChipSelection {
  const group = groups.find((each) => each.parameter === parameter);
  const shown = (group && effectiveListChipValues(group, selection)) ?? [];
  const next = shown.includes(value) ? shown.filter((shownValue) => shownValue !== value) : [...shown, value];
  return normalizeListChips(groups, { ...selection, [parameter]: next });
}

/** The selection with `value` the one value of its group, or none of them for undefined: a segmented control's choice. */
export function chooseListChip(
  groups: readonly ListChipGroup[],
  selection: ListChipSelection,
  parameter: string,
  value: string | undefined,
): ListChipSelection {
  return normalizeListChips(groups, { ...selection, [parameter]: value === undefined ? [] : [value] });
}

/**
 * What a group's menu button says it lets through: its `allLabel` for everything or the default, the
 * one value's label, two labels, or the first and how many more. A `short` summary, for a phone, names
 * one label and counts the rest from the second on.
 */
export function listChipSummary(group: ListChipGroup, selection: ListChipSelection, short = false): string {
  const chosen = selection[group.parameter] ?? [];
  const labels = group.chips.filter((chip) => chosen.includes(chip.value)).map((chip) => chip.label);
  if (labels.length === 0) return group.allLabel ?? "All";
  if (labels.length === 1 || (labels.length === 2 && !short)) return labels.join(", ");
  return `${labels[0]} +${labels.length - 1}`;
}

/** Whether any chip of any group is on. */
export function hasListChips(selection: ListChipSelection): boolean {
  return Object.values(selection).some((values) => values.length > 0);
}

/**
 * The values a group lets through: the chosen ones, or with none chosen the group's `sameAsNone`.
 * Undefined is no narrowing at all.
 */
export function effectiveListChipValues(group: ListChipGroup, selection: ListChipSelection): readonly string[] | undefined {
  const chosen = selection[group.parameter] ?? [];
  if (chosen.length > 0) return chosen;
  return group.sameAsNone;
}

/**
 * Whether a row whose field values `valueOf` answers is in the list `selection` shows: one of each
 * group's values (OR inside a group), in every group (AND across groups). For a list the browser
 * filters itself; a list the server filters sends the same selection as its query.
 */
export function listChipsMatch(
  groups: readonly ListChipGroup[],
  selection: ListChipSelection,
  valueOf: (parameter: string) => string | undefined,
): boolean {
  return groups.every((group) => {
    const values = effectiveListChipValues(group, selection);
    if (values === undefined) return true;
    const value = valueOf(group.parameter);
    return value !== undefined && values.includes(value);
  });
}

/**
 * The selection `searchParams` names, normalized. Values are matched without regard to case and
 * written back as the chip spells them; a value no chip has is ignored.
 */
export function readListChips(groups: readonly ListChipGroup[], searchParams: URLSearchParams): ListChipSelection {
  const selection: Record<string, readonly string[]> = {};
  for (const group of groups) {
    const named = searchParams.getAll(group.parameter).map((value) => value.toLowerCase());
    selection[group.parameter] = group.chips
      .filter((chip) => named.includes(chip.value.toLowerCase()))
      .map((chip) => chip.value);
  }
  return normalizeListChips(groups, selection);
}

/**
 * `searchParams` with each group's parameter replaced by `selection`, one repetition per value in
 * chip order. Every other parameter is kept where it was.
 */
export function writeListChips(
  groups: readonly ListChipGroup[],
  selection: ListChipSelection,
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const normalized = normalizeListChips(groups, selection);
  for (const group of groups) {
    next.delete(group.parameter);
    for (const value of normalized[group.parameter] ?? []) next.append(group.parameter, value);
  }
  return next;
}
